import {
    BORDER_HIDDEN,
    SelectField,
    STYLE_BASIC,
} from '@eventbrite/eds-input-field';
import { getSelectPropValues } from '@eventbrite/eds-select';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import React, { FunctionComponent } from 'react';
import type { Locale } from '../../api/models/locale';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';
import styles from './LanguageSelector.module.scss';
import newStyles from './LanguageSelectorRebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: styles,
    [REBRANDING_STYLES]: newStyles,
};

const getLanguages = (locales: Array<Locale>) => {
    const unsupportedLocales = [
        'en_IE',
        'en_NZ',
        'en_HK',
        'en_SG',
        'de_AT',
        'fr_BE',
        'nl_BE',
        'es_PE',
        'es_CL',
        'es_CO',
    ];

    return sortBy(
        // Don't display locales that aren't supported on Event Type/Language
        filter(locales, ({ locale }) => !unsupportedLocales.includes(locale)),
        'localized_language_name',
    );
};

export interface LanguageSelectorProps {
    defaultLocale: string;
    isAppRebrandActive?: boolean;
    locales: Array<Locale>;
    onChange?: (newValue: string, index: number) => void;
}

export const LanguageSelector: FunctionComponent<LanguageSelectorProps> = ({
    defaultLocale,
    locales,
    onChange,
    isAppRebrandActive = false,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    return (
        <div className={styles.languageLabelAndSelector}>
            <span
                className={classNames(styles.languageLabel, {
                    'eds-text-color--grey-600': !isAppRebrandActive,
                })}
                data-testid="language-selector-label"
            >
                {gettext('Language:')}
            </span>
            <SelectField
                borderType={BORDER_HIDDEN}
                customClassName={styles.select}
                defaultValue={defaultLocale}
                data-automation="language-dropdown"
                hideIcon={isAppRebrandActive}
                id="language-select-field"
                label={gettext('Select your language')}
                name="language-select-field"
                onChange={onChange}
                role={null}
                style={STYLE_BASIC}
                selectValueClassname={styles.select}
                values={getSelectPropValues(
                    getLanguages(locales),
                    'localized_language_name',
                    'locale',
                )}
                wrapperClassName={styles.borderSelect}
            />
        </div>
    );
};
